import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import { exhaustiveGuard } from '@libs/utils';
import type { TagData } from '@libs/ui';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type { ItinerarySegmentType } from './itinerarySegmentType';
import type {
  CarrierLogo,
  PortDetails,
  ProductInfo,
  ProductTagWithIcon,
  UnavailabilityReason,
} from './shared';

export enum FlightPriceType {
  OneWayOnly = 'oneWayOnly',
  OutboundHomebound = 'outboundHomebound',
  IncludedOnOutbound = 'includedOnOutbound',
}

export interface FlightCardDetails {
  carrier: FlightCarrierData | null;
  time: string;
  productLabels: ProductInfo[];
  warningTags: ProductTagWithIcon[];
  changeButtonText: string;
  flightPriceType: FlightPriceType;
  mainDetails?: string;
}

export interface FlightCarrierData {
  logo: CarrierLogo;
  name: string;
}

export interface FlightProductCardData {
  fromPort: PortDetails;
  toPort: PortDetails;
  details: FlightCardDetails;
}

export enum ItineraryFlightSegmentStatus {
  NoAvailable = 'noAvailable',
  Available = 'available',
  AlternativeFlight = 'alternativeFlight',
}

export interface AvailableItineraryFlightSegment {
  segmentType: ItinerarySegmentType.Flight;
  statusType: ItineraryFlightSegmentStatus.Available;
  productCardData: FlightProductCardData;
  ingredientId: IngredientId;
  suggestionType: SuggestionType | undefined;
  filtersPills: TagData[];
}

export interface UnavailableItineraryFlightSegment {
  segmentType: ItinerarySegmentType.Flight;
  statusType: ItineraryFlightSegmentStatus.NoAvailable;
  productCardData: null;
  ingredientId: IngredientId;
  reasons: UnavailabilityReason[];
  suggestionType: SuggestionType | undefined;
  filtersPills: TagData[];
}

export interface AlternativeItineraryFlightSegment {
  segmentType: ItinerarySegmentType.Flight;
  statusType: ItineraryFlightSegmentStatus.AlternativeFlight;
  productCardData: FlightProductCardData;
  ingredientId: IngredientId;
  reasons: UnavailabilityReason[];
  suggestionType: SuggestionType | undefined;
  filtersPills: TagData[];
}

export type ItineraryFlightSegment =
  | AvailableItineraryFlightSegment
  | UnavailableItineraryFlightSegment
  | AlternativeItineraryFlightSegment;

export function isAvailableFlightSegment(
  segment: ItineraryFlightSegment
): segment is AvailableItineraryFlightSegment {
  switch (segment.statusType) {
    case ItineraryFlightSegmentStatus.Available:
      return true;

    case ItineraryFlightSegmentStatus.AlternativeFlight:
    case ItineraryFlightSegmentStatus.NoAvailable:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}

export function isUnavailableFlightSegment(
  segment: ItineraryFlightSegment
): segment is UnavailableItineraryFlightSegment {
  switch (segment.statusType) {
    case ItineraryFlightSegmentStatus.NoAvailable:
      return true;

    case ItineraryFlightSegmentStatus.AlternativeFlight:
    case ItineraryFlightSegmentStatus.Available:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}
export function isAlternativeFlightSegment(
  segment: ItineraryFlightSegment
): segment is AlternativeItineraryFlightSegment {
  switch (segment.statusType) {
    case ItineraryFlightSegmentStatus.AlternativeFlight:
      return true;

    case ItineraryFlightSegmentStatus.NoAvailable:
    case ItineraryFlightSegmentStatus.Available:
      return false;

    default:
      exhaustiveGuard(segment, 'Not known segment type');
  }
}
